import FiberManualRecord from '@mui/icons-material/FiberManualRecord';
import { Grid, Paper, Typography } from '@mui/material';
import { useAnalyzers } from 'features/analyzers/api/getAnalyzers';
import { memo, useEffect, useState } from 'react';

import { useIsOperator } from '../auth/hooks/useIsOperator';
import { useAllSnapshotHistorySettings } from './api/getAllSnapshotHistorySettings';
import RecordingStatusContent from './RecordingStatusContent';

import { RecordingStatusRow } from './types';
import { getRecordingStatus } from './utils/recordingStatusUtils';

const RecordingStatusWidget = memo(() => {
  const isOperator = useIsOperator();
  const { data: analyzers } = useAnalyzers({});
  const [recordingStatusRows, setRecordingStatusRows] = useState<
    RecordingStatusRow[]
  >([]);

  const { data: historySettings, isLoading } = useAllSnapshotHistorySettings({
    config: { refetchInterval: 60000 },
  });

  // Every time historySettings changes, parse the new data
  useEffect(() => {
    if (!analyzers || analyzers.length === 0 || !isOperator) return;
    if (!historySettings || historySettings.length < 1) {
      setRecordingStatusRows([]);
      return;
    }
    const newRecordingStatusRows: RecordingStatusRow[] = [];
    for (const historySetting of historySettings) {
      const snapshot = historySetting.snapshot;
      if (!snapshot) continue;
      const analyzer = analyzers.find(
        (analyzer) => analyzer.id === snapshot.analyzer_id
      );
      const analyzerMode = analyzer?.mode;
      const analyzerActiveSnapshotId = analyzer?.active_snapshot;
      if (analyzerMode === undefined || analyzerActiveSnapshotId === undefined)
        continue;
      const recordingStatus = getRecordingStatus({
        id: snapshot.id,
        enabled: snapshot.enabled,
        analyzerMode,
        analyzerActiveSnapshotId,
        historySettings: historySetting,
      });
      if (recordingStatus === 'None') continue;
      newRecordingStatusRows.push({
        id: snapshot.id,
        analyzerId: analyzer?.id,
        name: snapshot.name,
        recordingStatus,
        locked: !!snapshot.recording_locked,
      });
    }
    setRecordingStatusRows(newRecordingStatusRows);
  }, [analyzers, isOperator, historySettings]);

  if (!isOperator) return null;

  return (
    <Grid item xs={12} container component={Paper} justifyContent={'center'}>
      <Grid item container xs={12} alignItems='center' justifyContent='center'>
        <FiberManualRecord color='error' />
        <Grid item style={{ marginLeft: '.3rem' }}>
          <Typography
            component='h4'
            variant='h6'
            style={{ textAlign: 'center' }}
          >
            Recording Snapshots
          </Typography>
        </Grid>
      </Grid>
      <Grid
        container
        item
        xs={12}
        style={{ maxHeight: '16rem', overflow: 'auto' }}
      >
        <RecordingStatusContent
          loading={isLoading}
          recordingStatusRows={recordingStatusRows}
        />
      </Grid>
    </Grid>
  );
});

export default RecordingStatusWidget;
